// Open House — the new Post a Project's bundle steps (minimums.md, Bundles,
// Book writing and Last screen): Bundle → Format → Writer tier → Outline →
// Book details → Review & post.
//
// The bundle step asks only what sets the price or what the cover designer
// needs to decide whether to request: which bundle, the cover upgrade, source
// files (PSD) and the cover AI permission. The cover questionnaire moves to the
// cover brief after posting (piece 5). The manuscript steps are book writing's
// (bookWritingSteps.js), driven with today's bundle flags (ctx.bundle,
// ctx.bundleForceFiction) so today's bundle rules apply: the 10,000-word
// minimum, no inline outline, fiction locked for Ultimate Fiction.
//
// A bundle that includes an outline (Platinum, Ultimate Fiction, Narration)
// shows it as included; an AI-generated manuscript has no outline step, as for
// book writing. A human-written Silver or Gold bundle has the old wizard's
// outline choice (book_writing/_three.html.erb): upgrade to the Platinum
// bundle, which only changes the bundle tier (the server respawns the bundle's
// postings, JobPostings::OpenHouseRebundle) and never
// sets add_outline, or supply my own outline, exactly as book writing does.
//
// Every answer lives in the page's one `posting`; bundleState() splits it into
// today's bundle shape (lib/post/bundle.js, serialize.js) for the price, the
// rail and the save.

import { coverDesign } from "@/lib/post/services/coverDesign"
import { RATES } from "@/lib/post/options"
import {
  BUNDLE_COMPOSITION, PLATINUM, SILVER, bundleCoverDelta, bundleEstimateLines, bundlePricePayload,
} from "@/lib/post/bundle"
import {
  DEFAULTS, ISSUE_ALIASES, money, openHouseSteps, railWith, writingLevels,
} from "./bookWritingSteps"
import { WORDING } from "./wording"

// The bundle's own answers in the page's posting; everything else is the manuscript's.
const BUNDLE_KEYS = ["bundle_tier", "cover_package_type", "cover_add_ons", "cover_ai", "character_voice_count"]

export const BUNDLE_DEFAULTS = { ...DEFAULTS, cover_package_type: SILVER, cover_ai: false }

// Cover issues name today's cover fields; the bundle step asks them under its own keys.
export const BUNDLE_ALIASES = { ...ISSUE_ALIASES, package_type: "cover_package_type", add_ons: "cover_add_ons" }

const composition = (values) => BUNDLE_COMPOSITION[values.bundle_tier] || null
const isFiction = (values, ctx) => !!ctx?.bundleForceFiction || values.fiction === true

// Today's bundle state from the page's posting: the manuscript, the cover
// (package, PSD, AI permission: unticked is "none"), and the narration voice
// count. A Platinum cover saves its included source files as add_ons, as
// today's server does (after_update_cover_design__two), so its order includes
// them; they are not charged (bundle_cover_cost). Outline collaboration keeps
// today's default (the writer brief asks it).
export function bundleState(posting = {}) {
  const manuscript = Object.fromEntries(Object.entries(posting).filter(([key]) => !BUNDLE_KEYS.includes(key)))
  const pkg = posting.cover_package_type || SILVER

  return {
    bundleTier: posting.bundle_tier ?? null,
    postings: {
      book_writing: manuscript,
      cover_design: { package_type: pkg, add_ons: pkg === PLATINUM || !!posting.cover_add_ons, ai_consent: posting.cover_ai ? "all" : "none" },
    },
    bundleExtras: { collaborative: true, character_voice_count: posting.character_voice_count ?? undefined, files: {} },
  }
}

// A resumed bundle draft (hydrateFromDraft) back into the page's posting.
export function bundlePosting(hydrated) {
  const cover = hydrated.postings.cover_design || {}
  return {
    ...hydrated.postings.book_writing,
    bundle_tier: hydrated.bundleTier,
    cover_package_type: cover.package_type,
    cover_add_ons: cover.package_type !== PLATINUM && !!cover.add_ons,
    cover_ai: cover.ai_consent === "all",
    character_voice_count: hydrated.bundleExtras?.character_voice_count ?? null,
  }
}

// Today's bundle cascade: Ultimate Fiction locks the manuscript to fiction
// (always human-written).
export function bundleCascade(next, key, value) {
  if (key === "bundle_tier" && BUNDLE_COMPOSITION[value]?.forceFiction) Object.assign(next, { fiction: true, use_ai: false })
  return next
}

// ── the bundle step ──────────────────────────────────────────────────────────

const [coverPackage, , coverAddon] = coverDesign.steps()
const todayCover = (step, key) => step.fields.find((f) => f.key === key)

const BUNDLE_FIELD = {
  type: "bundle",
  key: "bundle_tier",
  // Today's stepper node for the bundle choice (EntryPicker).
  reviewLabel: "Bundle",
  issue: (values) => (values.bundle_tier ? null : WORDING.bundleError),
  reviewValue: (values, ctx) => (ctx?.bundles || []).find((b) => b.bundleTier === values.bundle_tier)?.name ?? null,
}

// Today's cover package choice, priced as today's bundle upgrade: Silver is
// included, Gold and Platinum add their difference from Silver.
const COVER_FIELD = {
  ...todayCover(coverPackage, "package_type"),
  key: "cover_package_type",
  label: coverPackage.title,
  help: coverPackage.sub,
  options: (v, ctx) =>
    todayCover(coverPackage, "package_type").options(v, ctx).map((o) => {
      const delta = bundleCoverDelta(o.value, ctx)
      return { ...o, price: delta > 0 ? `+${money(delta)}` : "Included" }
    }),
}

// Today's source files add-on; a Platinum cover includes them.
const PSD_FIELD = {
  ...todayCover(coverAddon, "add_ons"), key: "cover_add_ons", when: (v) => v.cover_package_type !== PLATINUM,
}
const PSD_INCLUDED = {
  type: "included", key: "cover_psd_included", text: "Source files (included with Platinum)", reviewLabel: coverAddon.title,
  reviewValue: () => "Included", when: (v) => v.cover_package_type === PLATINUM,
}

const COVER_AI_FIELD = { type: "toggle", key: "cover_ai", label: WORDING.coverAI }

const BUNDLE_STEP = {
  id: "bundle",
  // Today's bundle choice screen (EntryPicker): stepper node, heading and line.
  node: "Bundle",
  title: "Choose a bundle",
  sub: "Every bundle is built around a full, professionally written manuscript. Final price scales with your book's length, set when you configure the writing.",
  fields: [BUNDLE_FIELD, COVER_FIELD, PSD_FIELD, PSD_INCLUDED, COVER_AI_FIELD],
}

// Today's narration voice count (BundleDeliverables), for a fiction Narration bundle.
const VOICES_FIELD = {
  type: "count",
  key: "character_voice_count",
  label: "Number of distinct character voices",
  help: "How many characters need a distinct voice from your narrator?",
  required: true,
  min: 1,
  when: (v, ctx) => !!composition(v)?.narration && isFiction(v, ctx),
}

const OUTLINE_INCLUDED = {
  type: "included", key: "outline_included", text: WORDING.outlineIncluded, reviewLabel: WORDING.outlineHeading,
  reviewValue: () => WORDING.outlineIncluded,
}

// The old wizard's upgrade choice, at the Platinum bundle card's "From" price
// (CustomerPostProjectPresenter#bundle_from_price) and with its tagline.
function platinumUpgrade(values, ctx) {
  const platinum = (ctx?.bundles || []).find((bundle) => bundle.bundleTier === PLATINUM)
  const amount = platinum?.fromPrice?.amount
  return { value: "upgrade", label: "Upgrade To Our Platinum Bundle", price: amount && `Starting at ${amount}`, sub: platinum?.header }
}

// Ultimate Fiction hides the fiction choice, as today's bundle form does.
const lockable = (f) => (f.key === "fiction" ? { ...f, when: (v, ctx) => !ctx?.bundleForceFiction } : f)

export function bundleSteps(values = {}, ctx = {}) {
  const manuscript = openHouseSteps(values, ctx).flatMap((step) => {
    if (step.id === "format") return [{ ...step, fields: [...step.fields.map(lockable), VOICES_FIELD] }]
    if (step.id !== "outline") return [step]
    if (composition(values)?.outline) return [{ ...step, sub: undefined, fields: [OUTLINE_INCLUDED] }]
    return composition(values) ? [{ ...step, fields: [{ ...step.fields[0], paidOption: platinumUpgrade }] }] : []
  })

  return [BUNDLE_STEP, ...manuscript]
}

// ── price ────────────────────────────────────────────────────────────────────

// Today's bundle estimate (calculate_bundle_cost's mirror) and /post/price
// entry. The estimate leaves out the Silver or Gold source files charge, which
// bundle_cover_cost adds; it is added here at today's rate and label
// (coverDesign's estimate), so the open-to-every-level range, priced only here, matches it.
const BUNDLE_PRICE = {
  estimate: (values, ctx) => {
    const state = bundleState(values)
    const estimate = bundleEstimateLines(state, ctx)
    const cover = state.postings.cover_design
    if (estimate.lines.length && cover.add_ons && cover.package_type !== PLATINUM) {
      estimate.lines.push({ label: "Source files (PSD)", amount: ctx?.rates?.cover_add_on_cost ?? RATES.COVER_ADD_ON_COST })
    }
    const total = Math.round(estimate.lines.reduce((sum, line) => sum + (Number(line.amount) || 0), 0) * 100) / 100
    return { ...estimate, total }
  },
  pricePayload: (values) => bundlePricePayload(bundleState(values)),
}

export const bundleRail = (values, ctx) => railWith(BUNDLE_PRICE, values, ctx, writingLevels(values, ctx))
